import { TFunction } from 'i18next';

/**
 * Maps backend error messages to translated frontend messages
 */
export class ErrorMapper {
    private static errorMappings: Record<string, string> = {
        // Authentication errors
        'Invalid credentials': 'auth:errors.invalid_credentials',
        'Invalid credentials.': 'auth:errors.invalid_credentials',
        'These credentials do not match our records.': 'auth:errors.invalid_credentials',
        'Unauthenticated': 'auth:errors.unauthenticated',
        'Unauthenticated.': 'auth:errors.unauthenticated',
        'The provided credentials are incorrect.': 'auth:errors.invalid_credentials',

        // Registration errors
        'The email has already been taken.': 'auth:errors.email_taken',
        'The password confirmation does not match.': 'auth:errors.password_mismatch',

        // Validation errors - Laravel i18n codes
        'validation.required': 'Este campo é obrigatório',
        'validation.email': 'O email deve ser um endereço de email válido',
        'validation.unique': 'Este valor já está em uso',
        'validation.min.string': 'Este campo deve ter no mínimo 8 caracteres',
        'validation.password.mixed': 'A senha deve conter letras maiúsculas e minúsculas',
        'validation.password.numbers': 'A senha deve conter pelo menos um número',
        'validation.password.symbols': 'A senha deve conter pelo menos um símbolo',
        'validation.password.uncompromised': 'Esta senha apareceu em vazamentos de dados e não é segura',
        'validation.confirmed': 'A confirmação não corresponde',
        'validation.max.string': 'Este campo não pode ter mais que 255 caracteres',

        // Standard validation errors
        'The email field is required.': 'auth:errors.email_required',
        'The password field is required.': 'auth:errors.password_required',
        'The email must be a valid email address.': 'auth:errors.email_invalid',

        // Rate limiting. Duas strings porque as duas camadas do throttle de login
        // respondem diferente: 'Too many login attempts.' vem do AuthController
        // (limite por credencial) e 'Too Many Requests' e o 429 padrao do middleware
        // throttle do Laravel (envelope por IP).
        'Too many login attempts.': 'auth:errors.too_many_attempts',
        'Too Many Requests': 'auth:errors.too_many_attempts',

        // Network errors
        'Network Error': 'auth:errors.network_error',
        'Request failed with status code 500': 'auth:errors.server_error',
        'Request failed with status code 422': 'auth:errors.validation_error',
        'Request failed with status code 401': 'auth:errors.unauthenticated',
        'Request failed with status code 403': 'auth:errors.unauthorized',
    };

    /**
     * Maps a backend error message to a translated message
     * @param errorMessage The error message from the backend
     * @param t Translation function
     * @param fallbackKey Optional fallback translation key
     * @returns Translated error message
     */
    static mapError(
        errorMessage: string,
        t: TFunction,
        fallbackKey?: string
    ): string {
        // Try to find exact match first
        const translationKey = this.errorMappings[errorMessage];

        if (translationKey) {
            return t(translationKey);
        }

        // Try partial matches for common patterns
        const lowerMessage = errorMessage.toLowerCase();

        if (lowerMessage.includes('invalid') && lowerMessage.includes('credential')) {
            return t('auth:errors.invalid_credentials');
        }

        if (lowerMessage.includes('too many')) {
            return t('auth:errors.too_many_attempts');
        }

        if (lowerMessage.includes('unauthenticated')) {
            return t('auth:errors.unauthenticated');
        }

        if (lowerMessage.includes('unauthorized')) {
            return t('auth:errors.unauthorized');
        }

        if (lowerMessage.includes('network')) {
            return t('auth:errors.network_error');
        }

        if (lowerMessage.includes('server') || lowerMessage.includes('500')) {
            return t('auth:errors.server_error');
        }

        // Use fallback key if provided
        if (fallbackKey) {
            return t(fallbackKey);
        }

        // Return original message if no mapping found
        return errorMessage;
    }

    /**
     * Maps multiple validation errors to translated messages
     * @param errors Object containing validation errors
     * @param t Translation function
     * @returns Object with translated error messages
     */
    static mapValidationErrors(
        errors: Record<string, string[]>,
        t: TFunction
    ): Record<string, string[]> {
        const mappedErrors: Record<string, string[]> = {};

        for (const [field, messages] of Object.entries(errors)) {
            mappedErrors[field] = messages.map(message =>
                this.mapError(message, t)
            );
        }

        return mappedErrors;
    }
}

export default ErrorMapper;
